import { useEffect, useId, useRef, useState } from 'react';
import { MAX_NOTE_LENGTH } from '../../core/exam-engine/state.ts';
import { useI18n } from '../../i18n/index.tsx';
import { Button } from '../../ui/primitives.tsx';

/**
 * A place to write down what you were thinking.
 *
 * Collapsed until asked for, because an empty textarea under every question is a permanent
 * invitation to do something other than answer it. Once a note exists it stays open — a note you
 * cannot see is a note you will not remember to reread.
 *
 * Uncontrolled from the session's point of view in one specific way: the reducer is told on every
 * keystroke, which is what makes a note survive navigating away mid-sentence and appear in a saved
 * session without a save step. `SET_NOTE` returns the same state for an unchanged note, so the
 * noisiest event in the app still cannot cause a pointless re-render.
 *
 * Callers pass `key={questionId}`, which is what resets "is the editor open" when the question
 * changes. Remounting rather than syncing: an effect that reset the state would reopen the editor
 * on the keystroke that empties a note, and would be a cascading render besides.
 */
export function QuestionNote({
  note,
  onChange,
  readOnly = false,
}: {
  readonly note: string;
  readonly onChange: (text: string) => void;
  readonly readOnly?: boolean;
}) {
  const { t } = useI18n();
  const fieldId = useId();
  const [open, setOpen] = useState(note !== '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const shouldFocus = useRef(false);

  useEffect(() => {
    if (shouldFocus.current && open) {
      shouldFocus.current = false;
      textareaRef.current?.focus();
    }
  }, [open]);

  if (readOnly && note === '') {
    return null;
  }

  if (!open) {
    return (
      <div className="note">
        <Button
          variant="quiet"
          onClick={() => {
            shouldFocus.current = true;
            setOpen(true);
          }}
        >
          {t('note.add')}
        </Button>
      </div>
    );
  }

  return (
    <div className="note note--open">
      <label className="note__label" htmlFor={fieldId}>
        {t('note.label')}
      </label>

      {readOnly ? (
        <p className="note__text">{note}</p>
      ) : (
        <>
          <textarea
            id={fieldId}
            ref={textareaRef}
            className="note__input"
            value={note}
            rows={3}
            maxLength={MAX_NOTE_LENGTH}
            placeholder={t('note.placeholder')}
            onChange={(event) => {
              onChange(event.target.value);
            }}
          />
          <p className="note__help">
            {t('note.help')}
            {note.length > MAX_NOTE_LENGTH - 200 && (
              <span className="note__count">
                {' '}
                {t('note.remaining', { count: MAX_NOTE_LENGTH - note.length })}
              </span>
            )}
          </p>
        </>
      )}
    </div>
  );
}
